import b from 'b_';
import cx from 'classnames';
import { Button, H3, Modal, ModalCloseProps, SimpleCallback, SimpleText, Text } from 'platform-components';
import { TEXT_MARKUP_TYPE } from 'platform-components/constants';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';
import React, { useMemo } from 'react';

import './confirmation-dialog.scss';

enum THEMES {
    DEFAULT = 'default',
    YANDEX = 'yandex',
}

export interface ConfirmationDialogProps {
    cancelInProgress?: boolean;
    closable?: boolean;
    confirmInProgress?: boolean;
    confirmLabelId?: string;
    disabled?: boolean;
    header?: string;
    headerId?: string;
    headerType?: TEXT_MARKUP_TYPE;
    headerValues?: Record<string, string | number | undefined>;
    message?: string;
    messageId?: string;
    messageValues?: Record<string, string | number | undefined>;
    messageType: TEXT_MARKUP_TYPE;
    mix?: string;
    onClose?: SimpleCallback;
    onConfirm?: SimpleCallback;
    onReject?: SimpleCallback;
    opened: boolean;
    rejectDisabled?: boolean;
    rejectLabelId?: string;
    theme?: THEMES;
    invertButtonsViewStyles?: boolean;
}

// eslint-disable-next-line complexity
export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
    const {
        closable,
        confirmLabelId,
        disabled,
        headerType,
        messageType,
        headerValues,
        rejectDisabled,
        messageId,
        messageValues,
        mix,
        onClose,
        onConfirm,
        onReject,
        opened,
        rejectLabelId,
        headerId,
        header,
        message,
        theme,
        confirmInProgress,
        cancelInProgress,
        invertButtonsViewStyles = false,
    } = props;

    const className = b('confirmation-dialog', { theme });
    const hasHeader = useMemo(() => Boolean(headerId || header), [headerId, header]);
    const hasMessage = useMemo(() => Boolean(messageId || message), [messageId, message]);

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
            )}
            hasClose={closable}
            mix={cx(className, mix)}
            onRequestClose={onClose}
            opened={opened}
            theme={Modal.THEMES.TEACHER}
        >
            {hasHeader && (
                <Modal.Header>
                    {headerId && (
                        <div className="confirmation-dialog__title">
                            <H3 id={headerId} type={headerType} values={headerValues} weight={Text.WEIGHT.M} />
                        </div>
                    )}
                    {header && (
                        <div className="confirmation-dialog__title">
                            <H3 type={headerType} weight={Text.WEIGHT.M}>
                                {header}
                            </H3>
                        </div>
                    )}
                </Modal.Header>
            )}
            {hasMessage && (
                <Modal.Content>
                    {messageId && (
                        <div className="confirmation-dialog__message">
                            <Text id={messageId} type={messageType} values={messageValues} />
                        </div>
                    )}
                    {message && (
                        <div className="confirmation-dialog__message">
                            <Text type={messageType}>{message}</Text>
                        </div>
                    )}
                </Modal.Content>
            )}
            <Modal.Footer>
                <Button
                    className="confirmation-dialog__button"
                    disabled={disabled}
                    onClick={onConfirm}
                    progress={confirmInProgress}
                    view={invertButtonsViewStyles ? Button.VIEW.SECONDARY : Button.VIEW.ACTION}
                >
                    <SimpleText id={confirmLabelId} />
                </Button>
                <Button
                    className="confirmation-dialog__button qa_cancel-button"
                    disabled={disabled || rejectDisabled}
                    onClick={onReject}
                    progress={cancelInProgress}
                    view={invertButtonsViewStyles ? Button.VIEW.ACTION : Button.VIEW.SECONDARY}
                >
                    <SimpleText id={rejectLabelId} />
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ConfirmationDialog.THEMES = THEMES;
ConfirmationDialog.defaultProps = {
    confirmLabelId: 'confirmationDialog.confirm',
    rejectLabelId: 'confirmationDialog.reject',
    theme: THEMES.DEFAULT,
};
